// src/hooks/useWalkingTour.ts
//
// Imperative hook that calls the generateWalkingTour Cloud Function.
// The function returns 202 immediately and writes the result to the
// conversation doc — callers should subscribe to that doc (via the
// conversationStore) to see the result.

import { useCallback, useState } from 'react';
import { auth } from '../services/firebase';
import { useLocation } from './useLocation';
import { useAuthStore } from '../stores/authStore';
import { getLanguageHint } from '../utils/getLanguageHint';

const FUNCTION_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/generateWalkingTour';

export function useWalkingTour() {
  const { location } = useLocation();
  const profile = useAuthStore((s) => s.profile);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(
    async (params: {
      conversationId: string;
      focus?: string;
      conversationHint?: string;
    }): Promise<boolean> => {
      const user = auth.currentUser;
      if (!user) {
        setError('Sign in to create a walking tour');
        return false;
      }
      if (!location?.latitude || !location?.longitude) {
        setError('Location required to create a walking tour');
        return false;
      }
      setRequesting(true);
      setError(null);
      try {
        const token = await user.getIdToken();
        const resp = await fetch(FUNCTION_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_token: token,
            conversation_id: params.conversationId,
            focus: params.focus || '',
            conversation_hint: params.conversationHint || '',
            location: {
              latitude: location.latitude,
              longitude: location.longitude,
            },
            // Language hint forwarded to the cloud function so
            // waypoint titles, descriptions, and walking notes
            // appear in the user's language. Place names stay in
            // their original/local form.
            language: getLanguageHint(profile),
          }),
        });
        if (!resp.ok && resp.status !== 202) {
          const text = await resp.text().catch(() => '');
          throw new Error(`HTTP ${resp.status}: ${text.slice(0, 200)}`);
        }
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        console.warn('generateWalkingTour failed:', msg);
        setError(msg);
        return false;
      } finally {
        setRequesting(false);
      }
    },
    [location, profile]
  );

  return { generate, requesting, error };
}
